import React from 'react';
import { Plus, Check, Clock } from 'lucide-react';
import {
  RawShopEntry,
  ParsedShopItem,
  calculateColumnSpan,
  calculateCardBackground,
  getSlidingCategoryTag,
  isLeavingToday,
  parseRawEntryItem,
} from '../../utils/fortniteShopParser';
import { SupportedCurrency } from '../../types';
import { formatPrice, calculateFiatPrice } from '../../utils/currency';

export function getCategoryTagLabel(item: any): string {
  if (!item) return 'OBJETO';
  if (item.isBundle || item.category === 'LOTES' || item.category === 'BUNDLES') {
    return 'LOTE';
  }
  const cat = (typeof item === 'string' ? item : item.category || '').toUpperCase().trim();
  if (cat.includes('SKIN') || cat.includes('TRAJE') || cat.includes('OUTFIT') || cat.includes('ATUENDO')) {
    return 'ATUENDO';
  }
  if (cat.includes('PICO') || cat.includes('HERRAMIENTA') || cat.includes('PICKAXE')) {
    return 'PICO';
  }
  if (cat.includes('GESTO') || cat.includes('BAILE') || cat.includes('EMOTE')) {
    return 'GESTO';
  }
  if (cat.includes('MOCHILA') || cat.includes('ACCESORIO') || cat.includes('BACKPACK')) {
    return 'ACCESORIO MOCHILERO';
  }
  if (cat.includes('ALA') || cat.includes('GLIDER')) {
    return 'ALADELTA';
  }
  if (cat.includes('PISTA') || cat.includes('IMPROVISACIÓN') || cat.includes('TRACK')) {
    return 'PISTA DE IMPROVISACIÓN';
  }
  if (cat.includes('VEHIC') || cat.includes('VEHÍC') || cat.includes('CAR')) {
    return 'VEHÍCULO';
  }
  if (cat.includes('PAPEL') || cat.includes('ENVOLTORIO') || cat.includes('WRAP')) {
    return 'PAPEL';
  }
  return cat || 'OBJETO';
}

export function resolveItemImage(item: any): string {
  const fallback = 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_glitch/icon.png';
  if (!item) return fallback;
  if (item.image && typeof item.image === 'string' && item.image.trim()) return item.image;
  if (item.images?.featured && typeof item.images.featured === 'string' && item.images.featured.trim()) return item.images.featured;
  if (item.images?.icon && typeof item.images.icon === 'string' && item.images.icon.trim()) return item.images.icon;
  if (item.images?.background && typeof item.images.background === 'string' && item.images.background.trim()) return item.images.background;
  return fallback;
}

export interface ItemCardProps {
  entry?: RawShopEntry;
  item?: any;
  inCart?: boolean;
  currency?: SupportedCurrency;
  isOrphan?: boolean;
  onToggleCart?: (entry: RawShopEntry) => void;
  onOpenDetail?: (entry: RawShopEntry) => void;
}

export const ItemCard: React.FC<ItemCardProps> = (props) => {
  const {
    inCart = false,
    currency = 'ARS',
    isOrphan = false,
    onToggleCart,
    onOpenDetail,
  } = props;

  const entry: RawShopEntry = props.entry || ({
    offerId: props.item?.id || 'item-fallback',
    finalPrice: props.item?.finalPrice || props.item?.vbucks || props.item?.price || 0,
    tileSize: props.item?.isBundle ? 'Size_2_x_1' : 'Size_1_x_1',
    bundle: props.item?.isBundle ? { name: props.item.name } : undefined,
  } as RawShopEntry);

  const item: ParsedShopItem = (props.item && props.item.name)
    ? {
        name: props.item.name,
        description: props.item.description || null,
        image: resolveItemImage(props.item),
        rarity: { displayValue: props.item.rarity || 'Común', value: props.item.rarity || 'common' },
        type: { displayValue: props.item.category || 'Cosmético', value: props.item.category || 'cosmetic' },
        isBundle: Boolean(props.item.isBundle),
        series: null,
        isBillboard: false,
      }
    : (parseRawEntryItem(entry) || {
        name: 'Cosmético',
        description: null,
        image: resolveItemImage(null),
        isBundle: false,
        series: null,
        isBillboard: false,
        rarity: { displayValue: 'Común', value: 'common' },
        type: { displayValue: 'Cosmético', value: 'cosmetic' },
      });

  const span = calculateColumnSpan(entry);

  // Column span classes
  const colSpanClass = isOrphan
    ? 'col-span-2 lg:col-span-1'
    : span === 4
    ? 'col-span-2 lg:col-span-4'
    : span === 3
    ? 'col-span-2 lg:col-span-3'
    : span === 2
    ? 'col-span-2'
    : 'col-span-1';

  // Aspect ratio classes matching official Fortnite proportions
  let ratioClass = 'card-ratio-1';
  if (isOrphan) {
    ratioClass = 'card-ratio-orphan';
  } else if (span >= 4 || entry.tileSize === 'Size_4_x_1' || entry.tileSize === 'Size_4_x_2') {
    ratioClass = 'card-ratio-4';
  } else if (span === 3) {
    ratioClass = 'card-ratio-3';
  } else if (span === 2) {
    ratioClass = 'card-ratio-2';
  }

  const isBundle = Boolean(item.isBundle || entry.bundle || entry.layout?.displayType === 'billboard');
  const isOutfit = Boolean(
    item.type?.value === 'outfit' ||
      item.type?.backendValue === 'AthenaCharacter' ||
      entry.brItems?.some(
        (sub) => sub.type?.value === 'outfit' || sub.type?.backendValue === 'AthenaCharacter'
      )
  );

  const outfitCount = (entry.brItems || []).filter(
    (sub) => sub.type?.value === 'outfit' || sub.type?.backendValue === 'AthenaCharacter'
  ).length;
  const isMultiOutfitBundle = isBundle && outfitCount > 1;

  const bgGradient = calculateCardBackground(entry, item);
  const slidingTag = getSlidingCategoryTag(entry, item);
  const leavesToday = isLeavingToday(entry);

  // Fiat price calculation with recharge overrides support
  const fiatPrice = calculateFiatPrice(entry.finalPrice, currency, entry.offerId);
  const formattedFiat = formatPrice(fiatPrice, currency);

  const isCar =
    (entry.cars && entry.cars.length > 0) ||
    entry.layout?.id?.toLowerCase().includes('car') ||
    entry.layout?.category?.toLowerCase().includes('motores');
  const isCarBody = entry.cars?.some(
    (c) => c.type?.value === 'body' || c.type?.backendValue?.includes('Body')
  );
  const isCarBundle = isCar && (Boolean(entry.bundle) || isCarBody);

  const isJamTrack =
    !isBundle &&
    span === 1 &&
    Boolean(
      (entry.tracks && entry.tracks.length > 0) ||
        item.type?.value === 'music' ||
        item.type?.backendValue === 'AthenaMusicPack' ||
        entry.layout?.id?.startsWith('JT') ||
        entry.layout?.name?.toLowerCase().includes('improvisación')
    );

  const handleCardClick = () => {
    if (onOpenDetail) {
      onOpenDetail(entry);
    } else if (onToggleCart) {
      onToggleCart(entry);
    }
  };

  const handleToggleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onToggleCart) {
      onToggleCart(entry);
    }
  };

  // 1. JAM TRACKS / PISTAS DE IMPROVISACIÓN (Square album cover)
  if (isJamTrack) {
    const artist = entry.tracks?.[0]?.artist || item.description || '';
    return (
      <div
        id={`shop-item-${entry.offerId}`}
        data-item-name={(item.name || '').toLowerCase()}
        data-item-id={(entry.brItems?.[0]?.id || '').toLowerCase()}
        data-offer-id={entry.offerId}
        role="button"
        tabIndex={0}
        aria-label={item.name}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        className={`shop-item-card-wrapper group relative pt-9 md:pt-14 flex flex-col cursor-pointer transition-transform duration-300 active:scale-[0.98] ${colSpanClass}`}
      >
        <div
          className={`relative flex-1 flex flex-col justify-between rounded-2xl md:rounded-[22px] shadow-xl p-2.5 md:p-4 shop-item-card ${ratioClass}`}
          style={{ background: bgGradient }}
        >
          {leavesToday && (
            <div className="absolute top-1.5 md:top-2 inset-x-2 md:inset-x-3 z-30 flex items-center justify-between gap-1 pointer-events-none">
              <span className="ml-auto inline-flex items-center gap-1 bg-red-600 text-white border border-red-400/40 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-wider shadow-sm font-chakra">
                <Clock className="w-2.5 h-2.5" />
                <span>Se va hoy</span>
              </span>
            </div>
          )}

          {/* Square Album Cover */}
          <div className="relative z-10 mx-auto -mt-5 md:-mt-[30px] w-[82%] aspect-square rounded-xl md:rounded-2xl overflow-hidden shadow-[0_12px_24px_rgba(0,0,0,0.55),0_4px_8px_rgba(0,0,0,0.35)] shrink-0 transition-transform duration-300 group-hover:scale-[1.02] flex items-center justify-center">
            <img
              src={item.image}
              alt={item.name}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-center"
              onError={(e) => {
                (e.target as HTMLImageElement).src =
                  'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?w=500&auto=format&fit=crop';
              }}
            />
          </div>

          {/* Máscara negra inferior */}
          <div
            className="absolute inset-x-0 bottom-0 h-32 md:h-40 pointer-events-none rounded-b-2xl md:rounded-b-[22px] bg-gradient-to-t from-black/90 via-black/50 to-transparent z-15"
            aria-hidden="true"
          />

          {/* Bottom text block translating up on hover */}
          <div className="relative z-20 pt-1 flex flex-col gap-0.5 md:gap-1 mt-auto transition-transform duration-200 ease-out group-hover:-translate-y-5 md:group-hover:-translate-y-6">
            {artist && (
              <p className="text-[10px] md:text-xs text-slate-200/90 font-bold uppercase tracking-wide truncate">
                {artist}
              </p>
            )}
            <h3 className="text-xs md:text-base font-black text-white uppercase italic tracking-tight line-clamp-1 group-hover:text-yellow-300 transition-colors font-chakra">
              {item.name}
            </h3>

            <div className="flex items-center justify-between mt-0.5 md:mt-1">
              <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                <div className="w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center text-[8px] md:text-[10px] text-black font-black italic shrink-0 shadow-sm font-chakra">
                  V
                </div>
                <div className="flex flex-col md:flex-row md:items-baseline leading-tight min-w-0">
                  <span className="font-black text-sm md:text-lg text-white tracking-tight font-chakra">
                    {entry.finalPrice.toLocaleString('es-AR')}
                  </span>
                  <span className="text-[9px] md:text-[10px] text-slate-300 font-bold md:ml-1 truncate font-chakra">
                    ≈ {formattedFiat}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleClick}
                className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-transform active:scale-90 shadow-md shrink-0 cursor-pointer ${
                  inCart
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-black hover:bg-yellow-400 hover:scale-110'
                }`}
                title={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
                aria-label={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
              >
                {inCart ? (
                  <Check className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
                ) : (
                  <Plus className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
                )}
              </button>
            </div>
          </div>

          {/* Sliding Bottom Tag */}
          <div className="absolute inset-x-0 bottom-0 h-6 md:h-7 overflow-hidden rounded-b-2xl md:rounded-b-[22px] pointer-events-none z-30">
            <div className="w-full h-full bg-[#0c0d12] border-t border-white/10 flex items-center justify-center transform transition-transform duration-200 ease-out translate-y-full group-hover:translate-y-0">
              <span className="text-[9px] md:text-[10px] font-black italic uppercase text-white tracking-wider px-2 truncate font-chakra">
                {slidingTag}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 2. BILLBOARDS (Panoramic bundle cards)
  if (item.isBillboard) {
    return (
      <div
        role="button"
        tabIndex={0}
        aria-label={item.name}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        className={`group relative pt-6 md:pt-8 flex flex-col cursor-pointer transition-transform duration-300 active:scale-[0.98] ${colSpanClass}`}
      >
        <div
          className={`relative flex-1 flex flex-col justify-between rounded-2xl md:rounded-[22px] overflow-hidden shadow-xl p-3.5 md:p-6 shop-item-card ${ratioClass}`}
          style={{ background: bgGradient }}
        >
          {item.bgImage && (
            <img
              loading="lazy"
              decoding="async"
              src={item.bgImage}
              alt="Fondo"
              className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent pointer-events-none z-10" />

          {(item.fgImage || item.image) && (
            <div className="absolute inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[62%] flex items-end justify-end pointer-events-none z-10 overflow-hidden">
              <img
                src={item.fgImage || item.image}
                alt={item.name}
                loading="lazy"
                decoding="async"
                className="w-auto h-full max-h-full object-contain object-right-bottom transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </div>
          )}

          <div className="relative z-20 flex items-center justify-between gap-1 pointer-events-none">
            <span className="inline-block bg-black text-yellow-400 border border-yellow-400/50 px-2.5 md:px-3 py-0.5 md:py-1 rounded-full text-[8px] md:text-[10px] font-black uppercase tracking-wider italic shadow-md font-chakra">
              LOTE
            </span>
            {leavesToday && (
              <span className="ml-auto inline-flex items-center gap-1 bg-red-600 text-white border border-red-400/40 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-wider shadow-lg font-chakra">
                <Clock className="w-2.5 h-2.5" />
                <span>Se va hoy</span>
              </span>
            )}
          </div>

          {/* Máscara negra inferior */}
          <div
            className="absolute inset-x-0 bottom-0 h-36 md:h-44 pointer-events-none rounded-b-2xl md:rounded-b-[22px] bg-gradient-to-t from-black/90 via-black/50 to-transparent z-15"
            aria-hidden="true"
          />

          <div className="relative z-20 flex flex-col gap-1 md:gap-1.5 mt-auto max-w-[70%] sm:max-w-[60%] md:max-w-[50%] transition-transform duration-200 ease-out group-hover:-translate-y-5 md:group-hover:-translate-y-6">
            {item.bodyImage && (
              <div className="mb-0.5 md:mb-1 pointer-events-none">
                <img
                  loading="lazy"
                  decoding="async"
                  src={item.bodyImage}
                  alt="Objetos incluidos"
                  className="h-5 md:h-8 object-contain"
                />
              </div>
            )}
            {item.titleImage ? (
              <img
                loading="lazy"
                decoding="async"
                src={item.titleImage}
                alt={item.name}
                className="h-6 md:h-12 w-auto max-w-full object-contain object-left mb-0.5 md:mb-1 pointer-events-none"
              />
            ) : (
              <h3 className="text-base md:text-2xl font-black text-white uppercase italic tracking-tight group-hover:text-yellow-300 transition-colors font-chakra">
                {item.name}
              </h3>
            )}
            {item.description && (
              <p className="text-[10px] md:text-sm text-slate-200 font-bold uppercase tracking-wider line-clamp-1 md:line-clamp-2">
                {item.description}
              </p>
            )}

            <div className="flex items-center gap-2 md:gap-4 mt-1 md:mt-2">
              <div className="flex items-center gap-1.5 md:gap-2">
                <div className="w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center text-[8px] md:text-[10px] text-black font-black italic shrink-0 shadow-sm font-chakra">
                  V
                </div>
                <div className="flex flex-col md:flex-row md:items-baseline leading-tight">
                  <span className="font-black text-base md:text-xl text-white tracking-tight font-chakra">
                    {entry.finalPrice.toLocaleString('es-AR')}
                  </span>
                  <span className="text-[9px] md:text-[10px] text-slate-300 font-bold md:ml-1 font-chakra">
                    ≈ {formattedFiat}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleClick}
                className={`w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center transition-transform active:scale-90 shadow-lg shrink-0 cursor-pointer ${
                  inCart
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-black hover:bg-yellow-400 hover:scale-110'
                }`}
                title={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
                aria-label={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
              >
                {inCart ? (
                  <Check className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
                ) : (
                  <Plus className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
                )}
              </button>
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 h-6 md:h-7 overflow-hidden rounded-b-2xl md:rounded-b-[22px] pointer-events-none z-30">
            <div className="w-full h-full bg-[#0c0d12] border-t border-white/10 flex items-center justify-center transform transition-transform duration-200 ease-out translate-y-full group-hover:translate-y-0">
              <span className="text-[9px] md:text-[10px] font-black italic uppercase text-white tracking-wider px-2 truncate font-chakra">
                {slidingTag}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 3. VEHÍCULOS / CARS
  if (isCarBundle) {
    return (
      <div
        id={`shop-item-${entry.offerId}`}
        data-item-name={(item.name || '').toLowerCase()}
        data-item-id={(entry.brItems?.[0]?.id || '').toLowerCase()}
        data-offer-id={entry.offerId}
        role="button"
        tabIndex={0}
        aria-label={item.name}
        onClick={handleCardClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
          }
        }}
        className={`shop-item-card-wrapper group relative pt-6 md:pt-8 flex flex-col cursor-pointer transition-transform duration-300 active:scale-[0.98] ${colSpanClass}`}
      >
        <div
          className={`relative flex-1 flex flex-col justify-between rounded-2xl md:rounded-[22px] overflow-hidden shadow-xl shop-item-card ${ratioClass}`}
          style={{ background: bgGradient }}
        >
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'https://images.unsplash.com/photo-1542751371-adc38448a05e?w=500&auto=format&fit=crop';
            }}
          />

          <div className="relative z-20 p-2.5 md:p-4 flex items-center justify-between gap-1 pointer-events-none">
            {isBundle && (
              <span className="inline-block bg-black text-yellow-400 border border-yellow-400/50 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-wider italic shadow-md font-chakra">
                LOTE
              </span>
            )}
            {leavesToday && (
              <span className="ml-auto inline-flex items-center gap-1 bg-red-600 text-white border border-red-400/40 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-wider shadow-lg font-chakra">
                <Clock className="w-2.5 h-2.5" />
                <span>Se va hoy</span>
              </span>
            )}
          </div>

          {/* Máscara negra inferior */}
          <div
            className="absolute inset-x-0 bottom-0 h-36 md:h-44 pointer-events-none rounded-b-2xl md:rounded-b-[22px] bg-gradient-to-t from-black/90 via-black/50 to-transparent z-15"
            aria-hidden="true"
          />

          <div className="relative z-20 p-2.5 md:p-4 pt-0 flex flex-col gap-1 mt-auto transition-transform duration-200 ease-out group-hover:-translate-y-5 md:group-hover:-translate-y-6">
            <h3 className="text-xs md:text-base font-black text-white uppercase italic tracking-tight line-clamp-1 group-hover:text-yellow-300 transition-colors font-chakra">
              {item.name}
            </h3>

            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                <div className="w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center text-[8px] md:text-[10px] text-black font-black italic shrink-0 shadow-sm font-chakra">
                  V
                </div>
                <div className="flex flex-col md:flex-row md:items-baseline leading-tight min-w-0">
                  <span className="font-black text-sm md:text-lg text-white tracking-tight font-chakra">
                    {entry.finalPrice.toLocaleString('es-AR')}
                  </span>
                  <span className="text-[9px] md:text-[10px] text-slate-300 font-bold md:ml-1 truncate font-chakra">
                    ≈ {formattedFiat}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleClick}
                className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-transform active:scale-90 shadow-md shrink-0 cursor-pointer ${
                  inCart
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-black hover:bg-yellow-400 hover:scale-110'
                }`}
                title={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
                aria-label={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
              >
                {inCart ? (
                  <Check className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
                ) : (
                  <Plus className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
                )}
              </button>
            </div>
          </div>

          <div className="absolute inset-x-0 bottom-0 h-6 md:h-7 overflow-hidden rounded-b-2xl md:rounded-b-[22px] pointer-events-none z-30">
            <div className="w-full h-full bg-[#0c0d12] border-t border-white/10 flex items-center justify-center transform transition-transform duration-200 ease-out translate-y-full group-hover:translate-y-0">
              <span className="text-[9px] md:text-[10px] font-black italic uppercase text-white tracking-wider px-2 truncate font-chakra">
                {slidingTag}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 4. ESTÁNDAR: SKINS, PICOS, LOTES, GESTOS, MOCHILAS, PAPELES
  // LA CLAVE: El contenedor padre tiene pt-7 md:pt-10 para permitir que la imagen sobresalga.
  // La tarjeta .shop-item-card NO TIENE overflow-hidden.
  // El contenedor de la imagen tiene -top-8 md:-top-10 para sobresalir hacia arriba,
  // y rounded-b-2xl md:rounded-b-[22px] overflow-hidden para recortarse a los lados y abajo.

  const isWide = span === 2;
  const isQuad = span >= 4 || entry.tileSize === 'Size_4_x_1' || entry.tileSize === 'Size_4_x_2';
  const isSingleCol = !isWide && !isQuad;

  let imageContainerClass =
    'absolute inset-x-0 -top-10 md:-top-14 bottom-0 pointer-events-none rounded-b-2xl md:rounded-b-[22px] overflow-hidden flex items-start justify-center z-10';
  let imageMaskStyle: React.CSSProperties = {};
  let imageClass = 'w-auto h-full max-w-[90%] object-contain';
  let imageInlineStyle: React.CSSProperties = {};

  if (isBundle && isSingleCol && !isOutfit) {
    imageContainerClass =
      'absolute inset-x-0 -top-10 md:-top-14 bottom-0 pointer-events-none rounded-b-2xl md:rounded-b-[22px] overflow-hidden flex items-center justify-center z-10 p-2';
    imageMaskStyle = {};
    imageClass = 'w-auto object-contain transition-transform duration-500 group-hover:scale-105';
    imageInlineStyle = {
      maxHeight: '84%',
      maxWidth: '92%',
      transform: 'scale(1.45) translateY(-4%)',
      transformOrigin: 'center 45%',
    };
  } else if (isOutfit || isBundle) {
    // Skins and Character Bundles: Use mask gradient fade at bottom
    imageMaskStyle = {
      WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 70%, transparent 92%)',
      maskImage: 'linear-gradient(to bottom, black 0%, black 70%, transparent 92%)',
    };

    if (isQuad) {
      imageClass = 'w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]';
    } else if (isMultiOutfitBundle) {
      imageClass = 'w-auto h-full max-w-[100%] object-contain object-top transition-transform duration-500 group-hover:scale-[1.04]';
      imageInlineStyle = {
        transform: isSingleCol ? 'scale(1.70) translateY(5%)' : 'scale(1.42) translateY(-7%)',
        transformOrigin: 'top center',
      };
    } else if (isBundle && isSingleCol) {
      imageClass = 'w-auto h-full max-w-none object-contain object-top transition-transform duration-500 group-hover:scale-[1.04]';
      imageInlineStyle = {
        transform: 'scale(1.25) translateY(2%)',
        transformOrigin: 'top center',
      };
    } else if (isOutfit) {
      // OFFICIAL SKIN SCALE & TRANSLATION
      const scaleVar = isWide ? 'var(--skin-scale-2col, 1.90)' : 'var(--skin-scale-1col, 1.80)';
      const translateVar = isWide ? 'var(--skin-translate-y-2col, -2%)' : 'var(--skin-translate-y-1col, -2%)';
      imageClass = 'w-auto h-full max-w-none object-contain object-top transition-transform duration-500 group-hover:scale-[1.04]';
      imageInlineStyle = {
        transform: `scale(${scaleVar}) translateY(${translateVar})`,
        transformOrigin: 'top center',
      };
    } else if (isBundle) {
      imageClass = 'w-auto h-full max-w-[95%] object-contain object-top transition-transform duration-500 group-hover:scale-[1.04]';
    }
  } else {
    // Non-outfits (Picks, gliders, emotes, backpacks, wraps): Centered with item scales
    imageContainerClass =
      'absolute inset-x-0 -top-10 md:-top-14 bottom-0 pointer-events-none rounded-b-2xl md:rounded-b-[22px] overflow-hidden flex items-center justify-center z-10 p-1 md:p-2';
    imageMaskStyle = {};
    const scaleVar = isWide ? 'var(--item-scale-2col, 1.20)' : 'var(--item-scale-1col, 1.45)';
    const translateVar = isWide ? 'var(--item-translate-y-2col, -4%)' : 'var(--item-translate-y-1col, -4%)';
    imageClass = 'w-auto object-contain transition-transform duration-300 group-hover:scale-110';
    imageInlineStyle = {
      maxHeight: isWide ? '75%' : '88%',
      maxWidth: isWide ? '94%' : '92%',
      transform: `scale(${scaleVar}) translateY(${translateVar})`,
      transformOrigin: 'center center',
    };
  }

  return (
    <div
      id={`shop-item-${entry.offerId}`}
      data-item-name={(item.name || '').toLowerCase()}
      data-item-id={(entry.brItems?.[0]?.id || '').toLowerCase()}
      data-offer-id={entry.offerId}
      data-is-bundle={isBundle ? 'true' : 'false'}
      role="button"
      tabIndex={0}
      aria-label={item.name}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      className={`shop-item-card-wrapper group relative pt-9 md:pt-14 flex flex-col cursor-pointer transition-transform duration-300 active:scale-[0.98] ${colSpanClass}`}
    >
      {/* Outer Card with locked ratio and Fortnite hover outline - NO overflow-hidden so image can pop out top */}
      <div
        className={`relative flex-1 flex flex-col justify-between rounded-2xl md:rounded-[22px] shadow-xl p-2.5 md:p-4 shop-item-card ${ratioClass}`}
        style={{ background: bgGradient }}
      >
        {/* Top Badges (LOTE, Se va hoy) */}
        {(isBundle || leavesToday) && (
          <div className="relative z-20 flex items-center justify-between gap-1 pointer-events-none">
            {isBundle && (
              <span className="inline-block bg-black text-yellow-400 border border-yellow-400/50 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-wider italic shadow-md font-chakra">
                LOTE
              </span>
            )}
            {leavesToday && (
              <span className="ml-auto inline-flex items-center gap-1 bg-red-600 text-white border border-red-400/40 px-2 md:px-2.5 py-0.5 md:py-1 rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-wider shadow-sm font-chakra">
                <Clock className="w-2.5 h-2.5" />
                <span>Se va hoy</span>
              </span>
            )}
          </div>
        )}

        {/* Render Image Container: Pops out the top, clipped at bottom/sides */}
        <div className={imageContainerClass} style={imageMaskStyle}>
          <img
            src={item.image}
            alt={item.name}
            loading="lazy"
            decoding="async"
            draggable={false}
            className={`item-render-image ${imageClass}`}
            style={imageInlineStyle}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png';
            }}
          />
        </div>

        {/* Máscara negra para oscurecer la parte inferior de las tarjetas (por encima del fondo de tarjeta, por debajo de textos) */}
        <div
          className="absolute inset-x-0 bottom-0 h-36 md:h-44 pointer-events-none rounded-b-2xl md:rounded-b-[22px] bg-gradient-to-t from-black/90 via-black/50 to-transparent z-15"
          aria-hidden="true"
        />

        {/* Bottom Metadata Block (Translates up on hover) */}
        <div className="item-metadata-block relative z-20 pt-1 flex flex-col gap-0.5 md:gap-1 mt-auto transition-transform duration-200 ease-out group-hover:-translate-y-5 md:group-hover:-translate-y-6">
          <h3 className="text-xs md:text-base font-black text-white uppercase italic tracking-tight line-clamp-1 group-hover:text-yellow-300 transition-colors font-chakra">
            {item.name}
          </h3>

          <div className="flex items-center justify-between mt-0.5 md:mt-1">
            <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
              <div className="w-4 h-4 md:w-5 md:h-5 bg-yellow-400 rounded-full flex items-center justify-center text-[8px] md:text-[10px] text-black font-black italic shrink-0 shadow-sm font-chakra">
                V
              </div>
              <div className="flex flex-col md:flex-row md:items-baseline leading-tight min-w-0">
                <span className="font-black text-sm md:text-lg text-white tracking-tight font-chakra">
                  {entry.finalPrice.toLocaleString('es-AR')}
                </span>
                <span className="text-[9px] md:text-[10px] text-slate-300 font-bold md:ml-1 truncate font-chakra">
                  ≈ {formattedFiat}
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleToggleClick}
              className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-transform active:scale-90 shadow-md shrink-0 cursor-pointer ${
                inCart
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-black hover:bg-yellow-400 hover:scale-110'
              }`}
              title={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
              aria-label={inCart ? 'Quitar del carrito' : 'Añadir al carrito'}
            >
              {inCart ? (
                <Check className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
              ) : (
                <Plus className="w-3 h-3 md:w-3.5 md:h-3.5 stroke-[3]" />
              )}
            </button>
          </div>
        </div>

        {/* Sliding Category Tag Bar (Reveals on hover from bottom) */}
        <div className="absolute inset-x-0 bottom-0 h-6 md:h-7 overflow-hidden rounded-b-2xl md:rounded-b-[22px] pointer-events-none z-30">
          <div className="item-sliding-tag w-full h-full bg-[#0c0d12] border-t border-white/10 flex items-center justify-center transform transition-transform duration-200 ease-out translate-y-full group-hover:translate-y-0">
            <span className="text-[9px] md:text-[10px] font-black italic uppercase text-white tracking-wider px-2 truncate font-chakra">
              {slidingTag}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCard;
